import { Box, HStack, VStack, Text } from "@chakra-ui/react";
import { cloneDeep } from "lodash";
import { useSelector } from "react-redux";
import {
  listAllCampaigns,
  selectCampaign,
} from "../../pages/dashboard/campaign/campaignSlice";
import {
  DynamicListChild,
  DynamicListChildL1,
  MEMBERSHIP_ASSET_TYPES,
  OperatorType,
} from "../../common/types/campaign";
import { FilterGroupBox } from "./FilterGroupBox";
import { ValueSelectFields } from "./DynamicListValueFields";
import { useContext, useEffect, useMemo } from "react";
import { SingleValue } from "react-select";
import DropdownWithSearch from "../DropdownWithSearch";
import { useAppDispatch } from "../../store";
import {
  getOperatorDetails,
  isLoading,
  isSuccess,
} from "../../common/helper/commonHelper";
import {
  listStaticListSummaryList,
  selectStaticList,
} from "../../pages/dashboard/staticlist/staticlistSlice";
import { AssetField } from "../../common/types/connection";
import {
  AssetNameWithTag,
  CustomAssetBadgeOption,
  CustomSingleValue,
} from "../OptionHelper";
import { DynamicListContext } from "./DynamicList";
import { FaRegListAlt } from "react-icons/fa";

export default function ListMembershipFilterGroup({
  data,
  label,
  id,
  onChange,
  onRemove,
  isReadOnly,
}: {
  data: DynamicListChild;
  label: string;
  id: string;
  onChange: (data: any) => void;
  onRemove: () => void;
  isReadOnly?: boolean;
}) {
  function onChildChange(child: DynamicListChildL1) {
    const dataCopy = cloneDeep(data);
    dataCopy.children[0] = child;
    onChange(dataCopy);
  }

  function onChangeFilterGroupName(value: string) {
    const dataCopy = cloneDeep(data);
    dataCopy.name = value;
    onChange(dataCopy);
  }

  const wrapperStyle = isReadOnly
    ? {
        py: "0",
        px: "0",
        spacing: "1",
      }
    : {
        py: "5",
        px: "3",
        spacing: "4",
      };

  return (
    <FilterGroupBox
      id={id}
      label={label}
      onRemove={onRemove}
      groupName={data.name}
      onGroupNameChange={onChangeFilterGroupName}
      icon={FaRegListAlt}
      isReadOnly={isReadOnly}
    >
      <VStack alignItems="flex-start" {...wrapperStyle} w="100%">
        {data.children && (
          <HStack width="100%" wrap="wrap" gridGap="2">
            <ActivitySelector
              data={data.children[0]}
              onChange={onChildChange}
              isReadOnly={isReadOnly}
            />
          </HStack>
        )}
      </VStack>
    </FilterGroupBox>
  );
}

function ActivitySelector({
  data,
  onChange,
  isReadOnly,
}: {
  data: DynamicListChildL1;
  onChange: (data: DynamicListChildL1) => void;
  isReadOnly?: boolean;
}) {
  const dispatch = useAppDispatch();

  const {
    campaignDetails,
    dynamicList: {
      operators: { data: operators },
    },
    totalCampaignList,
  } = useSelector(selectCampaign);
  const { activeErrorCheck } = useContext(DynamicListContext);

  const { staticListSummaryList } = useSelector(selectStaticList);

  const operatorDetails = useMemo(
    () =>
      getOperatorDetails(data.operator, operators, OperatorType.LIST_SPECIFIC),
    [data.operator, operators]
  );

  useEffect(() => {
    dispatch(listStaticListSummaryList());
    dispatch(listAllCampaigns());
  }, [dispatch]);

  const campaignOptions: AssetField[] = useMemo(() => {
    if (isSuccess(totalCampaignList.loading)) {
      const data = totalCampaignList.data
        .filter(
          (item) =>
            campaignDetails.data.campaign_id !== item.campaign_id &&
            (item.activated_at || item.deactivated_at)
        )
        .map((item) => {
          return {
            name: item.name,
            id: item.campaign_id,
            type: MEMBERSHIP_ASSET_TYPES.CAMPAIGN,
          };
        });
      return data;
    }
    return [];
  }, [campaignDetails.data.campaign_id, totalCampaignList]);

  const staticListOptions: AssetField[] = useMemo(() => {
    if (isSuccess(staticListSummaryList.loading)) {
      const data = staticListSummaryList.data.map((item) => {
        return {
          name: item.name,
          id: item.static_list_id,
          type: MEMBERSHIP_ASSET_TYPES.STATIC_LIST,
        };
      });
      return data;
    }
    return [];
  }, [staticListSummaryList]);

  const listOptions = useMemo(
    () => [...campaignOptions, ...staticListOptions],
    [campaignOptions, staticListOptions]
  );

  function onOperatorChange(operator: string) {
    const dataCopy = cloneDeep(data);
    if (dataCopy.operator !== operator) {
      dataCopy.value = [""];
    }
    dataCopy.operator = operator;
    if (operator) {
      dataCopy.validation_error = "";
    }
    onChange(dataCopy);
  }

  function onValueChange(val: SingleValue<AssetField>) {
    const dataCopy = cloneDeep(data);
    if (val && val.id) {
      dataCopy.validation_error = "";
      dataCopy.value = [val.id];
      dataCopy.filter = val.type;
    } else {
      dataCopy.validation_error = "Invalid values";
      dataCopy.value = [];
      dataCopy.filter = null;
    }
    onChange(dataCopy);
  }

  return (
    <HStack alignItems="center" spacing={isReadOnly ? "1" : "3"} w="100%">
      {isReadOnly ? (
        <Text>Membership</Text>
      ) : (
        <Text fontSize="sm">...membership property of...</Text>
      )}
      {operators && (
        <Box id="list-membership-activity-selector">
          <ValueSelectFields
            options={Object.values(operators[OperatorType.LIST_SPECIFIC]).map(
              (op) => ({
                label: op.display,
                value: op.id,
              })
            )}
            value={(data.operator as string) || ""}
            onChange={(value: string) => onOperatorChange(value)}
            validationError={activeErrorCheck ? data.validation_error : ""}
            isReadOnly={isReadOnly}
          />
        </Box>
      )}
      {data.operator &&
        operators &&
        operatorDetails &&
        (isReadOnly ? (
          <Box maxW="60%">
            <AssetNameWithTag
              data={
                listOptions.find((option) => option.id === data.value?.[0]) ??
                null
              }
              quotes
            />
          </Box>
        ) : (
          <DropdownWithSearch
            options={listOptions}
            value={
              listOptions.find((option) => option.id === data.value?.[0]) ??
              null
            }
            onChange={(val: SingleValue<AssetField>) => onValueChange(val)}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isSearchable={true}
            isLoading={
              isLoading(totalCampaignList.loading) ||
              isLoading(staticListSummaryList.loading)
            }
            isInvalid={!!data.validation_error}
            controlStyle={{
              height: "32px",
              minHeight: "32px",
              borderRadius: "2px",
            }}
            components={{
              Option: CustomAssetBadgeOption,
              SingleValue: CustomSingleValue,
            }}
            containerStyle={{
              width: "fit-content",
              maxWidth: "800px",
            }}
          />
        ))}
    </HStack>
  );
}
